import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import { useCallback, useRef, MouseEvent, memo } from 'react'
import {
  ClickAwayListener,
  Divider,
  ListItemButton,
  Paper,
  Popper,
  IconButton,
  Tooltip,
} from '@mui/material'
import { intl } from '../../../../../i18n'

type MenuItemListProps = {
  onRename: () => void
  label: string
  onDelete?: () => void
  deleteLabel?: string
  deleteDisabled?: boolean
  deleteDisabledReason?: string
}

type RenameMenuProps = MenuItemListProps & {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

const StyledMoreVertIcon = styled(MoreVertIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
  width: '16px',
})

const StyledIconButton = styled(IconButton)({
  padding: '4px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[2],
  },
})

const MenuItemListWrapper = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  display: 'flex',
  flexDirection: 'column',
  padding: '4px',
  borderRadius: '4px',
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  width: '230px',
})

const MenuItemListHeader = styled('div')({
  color: colorPalette.monotone[5],
  fontSize: '12px',
  fontWeight: 600,
  padding: '8px',
})

const MenuItemListDivider = styled(Divider)({
  borderColor: colorPalette.monotone[2],
})

const MenuItemListArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px',
  gap: '4px',
})

const MenuItem = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'alert',
})<{ alert?: boolean }>(({ alert }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  color: alert ? colorPalette.pink[5] : colorPalette.monotone[4],
  fontSize: '12px',
}))

const MenuItemList = memo(
  ({
    onRename,
    label,
    onDelete,
    deleteLabel,
    deleteDisabled,
    deleteDisabledReason,
  }: MenuItemListProps) => {
    return (
      <MenuItemListWrapper>
        <MenuItemListHeader>
          {intl.formatMessage({ id: 'menu' })}
        </MenuItemListHeader>
        <MenuItemListDivider />
        <MenuItemListArea>
          <MenuItem onClick={onRename}>{label}</MenuItem>
        </MenuItemListArea>
        {onDelete && (
          <>
            <MenuItemListDivider />
            <MenuItemListArea>
              {/* disabled な ListItemButton はマウスイベントを発火しないため span で包む */}
              <Tooltip
                title={
                  deleteDisabled && deleteDisabledReason
                    ? deleteDisabledReason
                    : ''
                }
              >
                <span>
                  <MenuItem
                    onClick={onDelete}
                    alert={true}
                    disabled={deleteDisabled}
                  >
                    {deleteLabel}
                  </MenuItem>
                </span>
              </Tooltip>
            </MenuItemListArea>
          </>
        )}
      </MenuItemListWrapper>
    )
  }
)

MenuItemList.displayName = 'MenuItemList'

export const RenameMenu = ({
  isOpen,
  onOpenChange,
  onRename,
  label,
  onDelete,
  deleteLabel,
  deleteDisabled,
  deleteDisabledReason,
}: RenameMenuProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      onOpenChange(!isOpen)
    },
    [isOpen, onOpenChange]
  )

  const handleRename = useCallback(() => {
    onOpenChange(false)
    onRename()
  }, [onOpenChange, onRename])

  const handleDelete = useCallback(() => {
    onOpenChange(false)
    onDelete?.()
  }, [onOpenChange, onDelete])

  const handleClickAway = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <>
      <StyledIconButton ref={buttonRef} onClick={onClick}>
        <StyledMoreVertIcon />
      </StyledIconButton>
      <Popper
        open={isOpen}
        anchorEl={buttonRef.current}
        placement="bottom-end"
        style={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={8}>
            <MenuItemList
              onRename={handleRename}
              label={label}
              onDelete={onDelete ? handleDelete : undefined}
              deleteLabel={deleteLabel}
              deleteDisabled={deleteDisabled}
              deleteDisabledReason={deleteDisabledReason}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
